import {FC, useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useQuery, useQueryClient} from 'react-query'
import {getExtraSetting, saveExtraSetting} from '../../../../../../_metronic/helpers'
import {useAuth} from '../../../../auth/core/Auth'
import {LOGIN_SESSION_STORAGE_PREFIX} from '../../../../auth/core/AuthHelpers'
import {getTermsStatus, getTermsStatusQueryKey} from '../../../../terms/core/_requests'
import {listPrintTemplates} from '../../print-studio/core/_requests'
import {printStudioEntryUrl, printTemplatesSettingsUrl} from '../../print-studio/core/studioUrls'
import {
  InvoicePrintPage,
  printPreferenceQueryKey,
  readPrintPreferenceError,
  saveInvoicePrintPreference,
  useInvoicePrintPreference,
} from '../core/printPagePreference'
import {shouldAnnouncePrintPage} from './announceAccess'
import {PrintPageIntroModal, PrintTemplateEditorEntry} from './PrintPageIntroModal'
import {PRINT_PAGE_INTRO_EVENT} from './printPageIntroEvent'

/** The user setting (api/auth/user/extrasetting) that keeps the announcement closed for good. */
export const PRINT_PAGE_INTRO_SETTING = 'printPageIntro'
/**
 * 2: the announcement also introduces the print-template designer. Whoever closed version 1 for
 * good sees it once more.
 */
export const INTRO_VERSION = 2

// Opens a moment after the panel, so the page underneath has appeared first.
const OPEN_DELAY_MS = 1200

type IntroSetting = {dismissed?: boolean; version?: number} | null | undefined

export const isIntroDismissed = (setting: IntroSetting) =>
  Boolean(setting && setting.dismissed && Number(setting.version || 0) >= INTRO_VERSION)

export {shouldAnnouncePrintPage}

// «متوجه شدم» closes it for this sign-in: signing out clears the key (LOGIN_SESSION_STORAGE_PREFIX),
// so the next sign-in in the same tab shows it again. Versioned, so a window of the previous
// version closed earlier does not hide this one.
export const introSessionKey = (userId: number) =>
  `${LOGIN_SESSION_STORAGE_PREFIX}print-page-intro-closed:v${INTRO_VERSION}:${userId}`

const readClosedThisSession = (userId: number) => {
  try {
    return window.sessionStorage.getItem(introSessionKey(userId)) === '1'
  } catch {
    return false
  }
}

const rememberClosedThisSession = (userId: number) => {
  try {
    window.sessionStorage.setItem(introSessionKey(userId), '1')
  } catch {
    // Without storage the window may open again after a reload, nothing worse.
  }
}

/**
 * Mounted once in the panel layout. Shows the announcement of the new print page and the
 * print-template designer to users who have not closed it for good, after the terms window (if
 * any) is done; its item in «امکانات تازه» opens it at any time (openPrintPageIntro). A failed read
 * never shows it half-filled: it simply does not open.
 */
export const PrintPageIntroGate: FC<{openDelayMs?: number}> = ({openDelayMs = OPEN_DELAY_MS}) => {
  const {currentUser, saveAuth, setCurrentUser} = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const userId = Number(currentUser?.id || 0)
  const companyId = Number(currentUser?.cpi || 0)
  const eligible = shouldAnnouncePrintPage(currentUser)

  const [closed, setClosed] = useState(() => readClosedThisSession(userId))
  // Asked for from the header: shown whether or not it was closed or dismissed before.
  const [requested, setRequested] = useState(false)
  const [ready, setReady] = useState(false)
  const [selected, setSelected] = useState<InvoicePrintPage>()
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>()

  useEffect(() => {
    const timer = window.setTimeout(() => setReady(true), openDelayMs)
    return () => window.clearTimeout(timer)
  }, [openDelayMs])

  useEffect(() => {
    setClosed(readClosedThisSession(userId))
  }, [userId])

  useEffect(() => {
    const open = () => {
      setSelected(undefined)
      setError(undefined)
      setRequested(true)
      setReady(true)
    }
    window.addEventListener(PRINT_PAGE_INTRO_EVENT, open)
    return () => window.removeEventListener(PRINT_PAGE_INTRO_EVENT, open)
  }, [])

  const setting = useQuery(
    ['user-setting', PRINT_PAGE_INTRO_SETTING, userId],
    () => getExtraSetting(PRINT_PAGE_INTRO_SETTING) as Promise<IntroSetting>,
    {enabled: eligible && !closed, staleTime: Infinity, refetchOnWindowFocus: false, retry: false}
  )
  const dismissed = isIntroDismissed(setting.data)
  const wanted = eligible && (requested || (!closed && setting.isSuccess && !dismissed))
  const preference = useInvoicePrintPreference(companyId, wanted)
  // Whether this user may open the designer, and on which template. The settings tab reads the
  // same list under the same key. A failed read only leaves out the button.
  const templates = useQuery(
    ['print-templates', companyId],
    ({signal}) => listPrintTemplates(companyId, signal),
    {
      enabled: wanted && companyId > 0,
      retry: false,
      refetchOnWindowFocus: false,
    }
  )
  // Only what the terms window already read: it opens first and this one waits for it.
  const terms = useQuery(
    getTermsStatusQueryKey(userId, companyId),
    ({signal}) => getTermsStatus(signal),
    {
      enabled: false,
    }
  )
  const termsOpen = Boolean(terms.data?.mustAccept)

  // Waits for the server to be able to store the choice it offers.
  const show =
    wanted &&
    ready &&
    preference.isSuccess &&
    Boolean(preference.data?.available) &&
    Number(preference.data?.companyId) === companyId &&
    (templates.isSuccess || templates.isError) &&
    !termsOpen

  if (!show || !preference.data) return null
  const current = preference.data
  const page = selected ?? current.page
  const editor: PrintTemplateEditorEntry = !templates.data
    ? {kind: 'unknown'}
    : !templates.data.canManage
    ? {kind: 'readOnly', reason: templates.data.readOnlyReason}
    : !templates.data.available
    ? {kind: 'notReady'}
    : {
        kind: 'open',
        url: printStudioEntryUrl(templates.data) ?? printTemplatesSettingsUrl(companyId),
      }

  const close = async () => {
    setClosed(true)
    setRequested(false)
    rememberClosedThisSession(userId)
    if (!dontShowAgain) return
    setDontShowAgain(false)
    try {
      const response = await saveExtraSetting(PRINT_PAGE_INTRO_SETTING, {
        dismissed: true,
        version: INTRO_VERSION,
        at: new Date().toISOString(),
      })
      queryClient.setQueryData(['user-setting', PRINT_PAGE_INTRO_SETTING, userId], {
        dismissed: true,
        version: INTRO_VERSION,
      })
      // The server answers with the session's renewed token, as for every user setting.
      if (response?.tk) {
        saveAuth(response)
        setCurrentUser(response)
      }
    } catch {
      // Not stored: the window opens again in a later session, where it can be closed again.
    }
  }

  /** Stores another default page when one was chosen; false when that failed (the reason shows). */
  const saveChoice = async () => {
    if (!current.canChange || page === current.page) return true
    setSaving(true)
    setError(undefined)
    try {
      const saved = await saveInvoicePrintPreference(companyId, page)
      queryClient.setQueryData(printPreferenceQueryKey(companyId), saved)
      return true
    } catch (saveError) {
      setError(
        readPrintPreferenceError(saveError, 'صفحهٔ چاپ پیش‌فرض ذخیره نشد. دوباره تلاش کنید.')
      )
      return false
    } finally {
      setSaving(false)
    }
  }

  const confirm = async () => {
    if (saving) return
    if (await saveChoice()) await close()
  }

  // Leaves as «متوجه شدم» would (the choice above kept), then opens the designer.
  const openEditor = async (url: string) => {
    if (saving) return
    if (!(await saveChoice())) return
    await close()
    navigate(url)
  }

  const businessName =
    currentUser?.cpl?.find((company) => Number(company.id) === companyId)?.nm?.trim() ||
    'کسب‌وکار انتخاب‌شده'

  return (
    <PrintPageIntroModal
      show
      businessName={businessName}
      preference={current}
      selected={page}
      onSelect={(next) => {
        setSelected(next)
        setError(undefined)
      }}
      dontShowAgain={dontShowAgain}
      onDontShowAgain={setDontShowAgain}
      saving={saving}
      error={error}
      editor={editor}
      onOpenEditor={openEditor}
      onConfirm={confirm}
      onClose={() => {
        if (!saving) close()
      }}
    />
  )
}
